const { DefaultAzureCredential, ClientSecretCredential } = require('@azure/identity');
const { ContainerClient } = require("@azure/storage-blob");
const { getCredential } = require('./auth/getCredentials');
const { AderantAzureStorageError } = require('./utils/errors');
const cache = require('node-cache');

let clientCache = new cache({ stdTTL: 3600, useClones: false });

/**
 * Get the client for the current context. This client will be cached for 1 hour upon creation.
 * If the client does not exist in the cache, it will be created using the options passed in
 * through the options.
 * @param {BlobClientOptions} blobClientOptions The options to get the client for
 * @returns @type {ContainerClient} Client 
 */
const getClient = (blobClientOptions) => {
    const cacheItemName = `${blobClientOptions.accountName}-${blobClientOptions.containerName}`
    let cacheItem = clientCache.get(cacheItemName);
    if (!cacheItem) {
        const blobClient = new ContainerClient(`https://${blobClientOptions.accountName}.blob.core.windows.net/${blobClientOptions.containerName}`, getCredential(blobClientOptions, 'blob'));
        clientCache.set(cacheItemName, blobClient);
        return blobClient;
    }
    return cacheItem;
}

const streamToBuffer = async (readableStream) => {
    return new Promise((resolve, reject) => {
        const chunks = [];
        readableStream.on("data", (data) => {
            chunks.push(data instanceof Buffer ? data : Buffer.from(data));
        });
        readableStream.on("end", () => {
            resolve(Buffer.concat(chunks));
        });
        readableStream.on("error", reject);
    });
}

/**
 * Blob store for accessing azure blob storage.
 */
class BlobStore {
    /**
     * Creates a blobStore instance. This is used for accessing the table stores in an Azure Table within a storage account.
     * 
     * The required parameters are one of the following
     *      tableName
     *      accountName
     * 
     * The optional combinations for authentication are the following:
     *      accountKey and accountName (for a shared key credential)
     *      clientSecret, clientId, tenantId (for a client secret credential)
     *      useIdentity set to true.
     * 
     * useIdentity will be used if available. If not, then we will check for the account key. Finally it will fall through to clientId, clientSecret and tenantId.
     * @param {object} blobClientOptions blob client options for client creation. 
     * Object structure:
     *  {
     *      accountKey: 'string',
     *      accountName: 'string',
     *      blobName: 'string',
     *      useIdentity: bool,
     *      clientSecret: 'string',
     *      clientId: 'string',
     *      tenantId: 'string'
     *  }
     */
    constructor(blobClientOptions) {
        if (!blobClientOptions) throw new AderantAzureStorageError('A blobClientOptions parameter is required when initializing the BlobStore class.');
        this.accountKey = blobClientOptions.accountKey;
        this.accountName = blobClientOptions.accountName;
        this.tenantId = blobClientOptions.tenantId;
        this.containerName = blobClientOptions.containerName;
        this.useIdentity = blobClientOptions.useIdentity;
        /**@type {ContainerClient} */
        this.blobClient = getClient(blobClientOptions);
    }

    /**Get the name of the storage account this blob store belongs to.
     * @returns {string} The name of the storage account.
     */
    getAccountName = () => {
        return this.blobClient.accountName;
    }

    /**Get the name of the blob container this blob store connects to.
     * @returns {string} The name of the blob container.
     */
    getContainerName = () => {
        return this.blobClient.containerName;
    }    

    /**
     * Lists blobs based on the given prefix.
     * @param {string} prefix prefix for the given blobs to list.
     * @returns 
     */
    listBlobs = async (prefix) => {
        const results = []
        if (prefix) {
            const blobIterator = this.blobClient.listBlobsFlat({ prefix: prefix });
            for await (let blob of blobIterator) {
                results.push(blob);
            }
            return results;
        }
        const blobIterator = this.blobClient.listBlobsFlat();
        for await (let blob of blobIterator) {
            results.push(blob);
        }
        return results;
    }

    /**
     * Get the blob content from the given path. 
     * @param {string} path The path of the blob to download, ex: client/Hal The AI/
     * @returns object {content: theContentOftheBlob, contentType: theContentTypeOftheBlob}
     */
    getBlobContent = async (path) => {
        const blockBlobClient = await this.blobClient.getBlobClient(path);
        const downloadResponse = await blockBlobClient.download();
        const downloaded = (
            await streamToBuffer(downloadResponse.readableStreamBody)
        );
        var contentType = downloadResponse.contentType;
        return { content: downloaded, contentType: contentType }
    }

    /**
     * Uploads a blob to the given path with the given content type.
     * @param {string} path path to upload the blob to inside storage.
     * @param {*} content the content to upload to blob storage
     * @param {*} contentType default: application/json, the content type of the blob to upload, this follows the common mime-type mappings: https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
     * @param {*} overwrite default: false, if true, the blob will be overwritten if it exists.
     * @returns 
     */
    uploadBlob = async (path, content, contentType = 'application/json', overwrite = false) => {
        const data = Buffer.from(content, 'utf-8');
        const blockBlobClient = this.blobClient.getBlockBlobClient(path);
        const uploadResponse = await blockBlobClient.upload(data, content.length, {
            blobHTTPHeaders: {
                blobContentType: contentType
            },
            overwrite: overwrite
        });
        return uploadResponse;
    }

    /**
     * Returns if the blob exists at the given path. True: I EXIST, False: I don't exist.
     * @param {string} path The path of the blob
     * @returns 
     */
    blobExists = async (path) => {
        const blockBlobClient = this.blobClient.getBlobClient(path);
        return await blockBlobClient.exists();
    }

    /**
     * Delete the blob at the given path.
     * @param {string} path delete the blob at the given path.
     * @returns bool: true if the blob was deleted or did not exist.
     */
    deleteBlob = async (path) => {
        if (!path) throw new AderantAzureStorageError(`You must provide a path for the blob to be deleted.`);
        const blockBlobClient = this.blobClient.getBlockBlobClient(path);
        const blobExists = await this.blobExists(path);
        if (blobExists) {
            await blockBlobClient.delete();
        }
        return true;
    }

    /**
     * Deletes all blobs that match the given prefix.
     * @param {string} prefix prefix of the blobs to be deleted. This is required.
     * @returns 
     */
    deleteBlobByPrefix = async (prefix) => {
        if (!prefix || prefix.split('/').length < 1) throw new AderantAzureStorageError(`Are you crazy? Why would you not provide a path to delete blobs in a storage account? Be better.`);
        const blobsToDelete = await this.listBlobs(prefix);
        for (let i = 0; i < blobsToDelete.length; i++) {
            const blockBlobClient = this.blobClient.getBlockBlobClient(blobsToDelete[i].name);
            const deleteResult = await this.blobClient.deleteBlob(blobsToDelete[i].name);
        }
        return blobsToDelete.length;
    }
}

module.exports = { BlobStore };